import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminService } from '../services/adminService';
import companyService from '../services/companyService';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';

const TABS = {
  users: 'users',
  clinics: 'clinics'
};

const styles = {
  page: {
    minHeight: '100%',
    background: '#f5f7fb',
    padding: '32px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    padding: '24px'
  },
  tabs: {
    display: 'flex',
    gap: '24px',
    borderBottom: '1px solid #d9e0ea',
    marginBottom: '24px'
  },
  tab: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    padding: '0 0 14px',
    marginBottom: '-1px',
    color: '#7a8699',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent'
  },
  tabActive: {
    color: 'var(--company-primary-color, #273947)',
    borderBottomColor: 'var(--company-primary-color, #273947)'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px'
  },
  sectionTitle: {
    margin: 0,
    color: '#1f2937',
    fontSize: '24px',
    fontWeight: 700
  },
  primaryButton: {
    appearance: 'none',
    border: '1px solid var(--company-primary-color, #273947)',
    background: 'var(--company-primary-color, #273947)',
    color: 'var(--company-primary-contrast, #ffffff)',
    borderRadius: '6px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  outlineButton: {
    appearance: 'none',
    border: '1px solid #d9e0ea',
    background: '#ffffff',
    color: '#475569',
    borderRadius: '6px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  dangerButton: {
    appearance: 'none',
    border: '1px solid #f1c6cb',
    background: '#fff5f5',
    color: '#dc3545',
    borderRadius: '6px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  warningButton: {
    appearance: 'none',
    border: '1px solid #ffd79a',
    background: '#fff7e6',
    color: '#c97a00',
    borderRadius: '6px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  tableWrap: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    borderBottom: '1px solid #d9e0ea',
    color: '#475569',
    fontSize: '13px',
    fontWeight: 700,
    background: '#fbfcfe'
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #eef2f7',
    color: '#1f2937',
    fontSize: '14px',
    verticalAlign: 'middle'
  },
  subtle: {
    color: '#7a8699'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    flexWrap: 'wrap'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 700
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
    color: '#7a8699'
  },
  loadingState: {
    textAlign: 'center',
    padding: '48px 16px',
    color: '#7a8699'
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    zIndex: 1100
  },
  modal: {
    width: '100%',
    maxWidth: '500px',
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 24px 48px rgba(15, 23, 42, 0.18)',
    padding: '24px'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px'
  },
  modalTitle: {
    margin: 0,
    color: '#1f2937',
    fontSize: '22px',
    fontWeight: 700
  },
  closeButton: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: '#64748b',
    fontSize: '28px',
    lineHeight: 1,
    cursor: 'pointer'
  },
  field: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#475569',
    fontSize: '14px',
    fontWeight: 600
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #d9e0ea',
    borderRadius: '6px',
    padding: '10px 12px',
    fontSize: '14px',
    color: '#1f2937',
    outline: 'none'
  },
  helper: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#7a8699'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '24px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    color: '#334155',
    fontSize: '14px',
    fontWeight: 500
  },
  linkButton: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: 'var(--company-primary-color, #273947)',
    padding: 0,
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600
  },
  supportIntro: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '22px',
    flexWrap: 'wrap'
  },
  searchInput: {
    width: '300px',
    maxWidth: '100%',
    boxSizing: 'border-box',
    border: '1px solid #d9e0ea',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '14px',
    color: '#1f2937',
    outline: 'none'
  },
  companyGroup: {
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '16px'
  },
  companyGroupHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '14px 16px',
    background: '#f8fafc',
    borderBottom: '1px solid #e5e7eb',
    flexWrap: 'wrap'
  },
  companyGroupTitle: {
    margin: 0,
    color: '#1f2937',
    fontSize: '15px',
    fontWeight: 700
  },
  companyGroupMeta: {
    marginTop: '3px',
    color: '#64748b',
    fontSize: '12px'
  },
  groupAddButton: {
    appearance: 'none',
    border: '1px solid #cbd5e1',
    background: '#ffffff',
    color: '#334155',
    borderRadius: '7px',
    padding: '7px 11px',
    fontSize: '12px',
    fontWeight: 650,
    cursor: 'pointer'
  }
};

function getRoleBadgeStyle(role) {
  return {
    ...styles.badge,
    background: role === 'superadmin' ? '#e0f2f7' : '#eef2f7',
    color: role === 'superadmin' ? 'var(--company-primary-color, #273947)' : '#526075'
  };
}

function getRoleLabel(role) {
  if (role === 'superadmin') return 'Platform Owner';
  if (role === 'companyadmin') return 'Company Admin';
  return 'Staff';
}

function getStatusBadgeStyle(user) {
  const enabled = user.twoFactorEnabled;
  return {
    ...styles.badge,
    background: enabled ? '#e8f7ee' : '#fff4e5',
    color: enabled ? '#2e7d32' : '#c97a00'
  };
}

export default function ManageUsersPage() {
  const { showAlert, showConfirm } = useModal();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyFilter = searchParams.get('company') || '';
  const isPlatformOwner = currentUser?.role === 'superadmin';
  const [activeTab, setActiveTab] = useState(TABS.users);
  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showAddClinicModal, setShowAddClinicModal] = useState(false);
  const [showEditClinicModal, setShowEditClinicModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingClinic, setEditingClinic] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    displayName: '',
    password: '',
    role: 'staff',
    clinicId: companyFilter,
    requiresTwoFactorSetup: false
  });
  const [newClinic, setNewClinic] = useState({ name: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Fetch assignments in one call rather than one request per clinic —
      // the per-clinic version issued 1 + N requests and could trip the API
      // rate limit, which then looked like a dropped session.
      const [usersData, clinicsData, assignments] = await Promise.all([
        adminService.getAllUsers(),
        isPlatformOwner ? companyService.getAll() : adminService.getAllClinics(),
        adminService.getAllClinicAssignments().catch(() => ({})),
      ]);
      setUsers(Array.isArray(usersData) ? usersData : []);

      // The clinics list endpoint does not include assigned form IDs, so the
      // "Templates" count showed 0. Merge the assignments in so it reflects reality.
      const baseClinics = Array.isArray(clinicsData) ? clinicsData : [];
      const assignmentMap = assignments && typeof assignments === 'object' ? assignments : {};
      const clinicsWithForms = baseClinics.map((clinic) => {
        const formIds = assignmentMap[clinic.id];
        return {
          ...clinic,
          assignedForms: Array.isArray(formIds)
            ? formIds
            : (Array.isArray(clinic.assignedForms) ? clinic.assignedForms : []),
        };
      });
      setClinics(clinicsWithForms);
    } catch (error) {
      console.error('Failed to load users and clinics:', error);
      await showAlert('Failed to load users and clinics', 'Error');
    } finally {
      setLoading(false);
    }
  };

  const clinicUsage = useMemo(() => {
    return clinics.reduce((acc, clinic) => {
      const count = users.filter(user => user.clinicId === clinic.id).length;
      acc[clinic.id] = count;
      return acc;
    }, {});
  }, [clinics, users]);

  const selectedCompany = useMemo(
    () => clinics.find((clinic) => clinic.id === companyFilter),
    [clinics, companyFilter],
  );

  const visibleUsers = useMemo(
    () => companyFilter ? users.filter((user) => user.clinicId === companyFilter) : users,
    [users, companyFilter],
  );

  const searchedUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return visibleUsers;
    return visibleUsers.filter((item) => [item.displayName, item.username, item.role, item.clinicName]
      .some((value) => String(value || '').toLowerCase().includes(term)));
  }, [visibleUsers, searchTerm]);

  const companyUserGroups = useMemo(() => {
    if (!isPlatformOwner || companyFilter) return [];
    const groups = [];
    const platformUsers = searchedUsers.filter((item) => item.role === 'superadmin');
    if (!searchTerm.trim() || platformUsers.length > 0) {
      groups.push({ id: 'platform', name: 'Platform Owners', code: 'PLATFORM', users: platformUsers });
    }
    clinics.forEach((clinic) => {
      const companyUsers = searchedUsers.filter((item) => item.clinicId === clinic.id && item.role !== 'superadmin');
      if (!searchTerm.trim() || companyUsers.length > 0) {
        groups.push({ id: clinic.id, name: clinic.name, code: clinic.code, domain: clinic.domain, users: companyUsers });
      }
    });
    const knownClinicIds = new Set(clinics.map((clinic) => clinic.id));
    const unassigned = searchedUsers.filter((item) => item.role !== 'superadmin' && (!item.clinicId || !knownClinicIds.has(item.clinicId)));
    if (unassigned.length > 0) groups.push({ id: 'unassigned', name: 'Unassigned users', code: 'REVIEW', users: unassigned });
    return groups;
  }, [clinics, searchedUsers, isPlatformOwner, companyFilter, searchTerm]);

  const getClinicName = (clinicId) => {
    if (!clinicId) return '-';
    const clinic = clinics.find(item => item.id === clinicId);
    return clinic?.name || '-';
  };

  const openAddUser = (clinicId = '') => {
    setNewUser({
      username: '',
      displayName: '',
      password: '',
      role: 'staff',
      clinicId: clinicId || companyFilter,
      requiresTwoFactorSetup: false
    });
    setShowAddUserModal(true);
  };

  const renderUsersTable = (rows, showCompany = false) => (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Username</th>
            <th style={styles.th}>Role</th>
            {showCompany && <th style={styles.th}>Company</th>}
            <th style={styles.th}>Status</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => (
            <tr key={item.id}>
              <td style={styles.td}>{item.displayName || '-'}</td>
              <td style={{ ...styles.td, ...styles.subtle }}>{item.username}</td>
              <td style={styles.td}><span style={getRoleBadgeStyle(item.role)}>{getRoleLabel(item.role)}</span></td>
              {showCompany && <td style={{ ...styles.td, ...styles.subtle }}>{getClinicName(item.clinicId)}</td>}
              <td style={styles.td}>
                <span style={getStatusBadgeStyle(item)}>
                  {item.twoFactorEnabled ? 'Active' : item.requiresTwoFactorSetup ? 'Setup Required' : 'Inactive'}
                </span>
              </td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                <div style={styles.actions}>
                  <button type="button" style={styles.outlineButton} onClick={() => handleEditUser(item)}>Edit</button>
                  {item.twoFactorEnabled && (
                    <button type="button" style={styles.warningButton} onClick={() => handleReset2FA(item.id, item.username)}>Reset 2FA</button>
                  )}
                  <button type="button" style={styles.dangerButton} onClick={() => handleDeleteUser(item.id, item.username)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <div style={{ ...styles.emptyState, padding: '24px 16px' }}>No users in this group.</div>}
    </div>
  );

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.displayName || !newUser.password) {
      await showAlert('Please fill in all required fields', 'Validation');
      return;
    }

    if (newUser.role !== 'superadmin' && !newUser.clinicId) {
      await showAlert('Please select a company for this user', 'Validation');
      return;
    }

    try {
      await adminService.createUser(newUser);
      setShowAddUserModal(false);
      setNewUser({
        username: '',
        displayName: '',
        password: '',
        role: 'staff',
        clinicId: companyFilter,
        requiresTwoFactorSetup: false
      });
      await loadData();
    } catch (error) {
      console.error('Failed to create user:', error);
      await showAlert(error.response?.data?.error || 'Failed to create user', 'Error');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      id: user.id,
      username: user.username,
      displayName: user.displayName || '',
      password: '',
      role: user.role,
      clinicId: user.clinicId || '',
      requiresTwoFactorSetup: Boolean(user.requiresTwoFactorSetup)
    });
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser?.username || !editingUser?.displayName) {
      await showAlert('Please fill in username and display name', 'Validation');
      return;
    }

    if (editingUser.role !== 'superadmin' && !editingUser.clinicId) {
      await showAlert('Please select a company for this user', 'Validation');
      return;
    }

    try {
      const payload = {
        username: editingUser.username,
        displayName: editingUser.displayName,
        role: editingUser.role,
        clinicId: editingUser.clinicId || null,
        requiresTwoFactorSetup: Boolean(editingUser.requiresTwoFactorSetup)
      };

      if (editingUser.password) {
        payload.password = editingUser.password;
      }

      await adminService.updateUser(editingUser.id, payload);
      setShowEditUserModal(false);
      setEditingUser(null);
      await loadData();
      await showAlert('User updated successfully', 'Success');
    } catch (error) {
      console.error('Failed to update user:', error);
      await showAlert(error.response?.data?.error || 'Failed to update user', 'Error');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    const ok = await showConfirm(`Are you sure you want to delete user "${username}"?`, {
      title: 'Delete User',
      danger: true
    });
    if (!ok) return;

    try {
      await adminService.deleteUser(userId);
      await loadData();
      await showAlert('User deleted successfully', 'Success');
    } catch (error) {
      console.error('Failed to delete user:', error);
      await showAlert(error.response?.data?.error || 'Failed to delete user', 'Error');
    }
  };

  const handleReset2FA = async (userId, username) => {
    const ok = await showConfirm(
      `Reset 2FA for user "${username}"? They will be required to set up 2FA again on their next login.`,
      { title: 'Reset 2FA', danger: false }
    );

    if (!ok) return;

    try {
      await adminService.reset2FA(userId);
      await loadData();
      await showAlert('2FA reset successfully. User will be required to set up 2FA on next login.', 'Success');
    } catch (error) {
      console.error('Failed to reset 2FA:', error);
      await showAlert(error.response?.data?.error || 'Failed to reset 2FA', 'Error');
    }
  };

  const handleAddClinic = async () => {
    if (!newClinic.name) {
      await showAlert('Please enter clinic name', 'Validation');
      return;
    }

    try {
      await adminService.createClinic(newClinic);
      setShowAddClinicModal(false);
      setNewClinic({ name: '' });
      await loadData();
    } catch (error) {
      console.error('Failed to create clinic:', error);
      await showAlert(error.response?.data?.error || 'Failed to create clinic', 'Error');
    }
  };

  const handleEditClinic = (clinic) => {
    setEditingClinic({
      id: clinic.id,
      name: clinic.name
    });
    setShowEditClinicModal(true);
  };

  const handleUpdateClinic = async () => {
    if (!editingClinic?.name) {
      await showAlert('Please enter clinic name', 'Validation');
      return;
    }

    try {
      await adminService.updateClinic(editingClinic.id, { name: editingClinic.name });
      setShowEditClinicModal(false);
      setEditingClinic(null);
      await loadData();
      await showAlert('Clinic updated successfully', 'Success');
    } catch (error) {
      console.error('Failed to update clinic:', error);
      await showAlert(error.response?.data?.error || 'Failed to update clinic', 'Error');
    }
  };

  const handleDeleteClinic = async (clinicId, clinicName) => {
    const ok = await showConfirm(`Are you sure you want to delete clinic "${clinicName}"?`, {
      title: 'Delete Clinic',
      danger: true
    });
    if (!ok) return;

    try {
      await adminService.deleteClinic(clinicId);
      await loadData();
      await showAlert('Clinic deleted successfully', 'Success');
    } catch (error) {
      console.error('Failed to delete clinic:', error);
      await showAlert(error.response?.data?.error || 'Failed to delete clinic', 'Error');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {companyFilter && (
          <div style={{ marginBottom: 18 }}>
            <button type="button" style={styles.outlineButton} onClick={() => navigate('/admin/companies')}>
              ← Back to companies
            </button>
            <div style={{ marginTop: 14 }}>
              <h2 style={{ ...styles.sectionTitle, fontSize: 20 }}>
                {selectedCompany?.name || 'Company'} user support
              </h2>
              <div style={styles.subtle}>Manage access, roles, passwords and two-factor recovery for this company.</div>
            </div>
          </div>
        )}

        {!companyFilter && !isPlatformOwner && <div style={styles.tabs}>
          <button
            type="button"
            style={{ ...styles.tab, ...(activeTab === TABS.users ? styles.tabActive : null) }}
            onClick={() => setActiveTab(TABS.users)}
          >
            User Management
          </button>
          <button
            type="button"
            style={{ ...styles.tab, ...(activeTab === TABS.clinics ? styles.tabActive : null) }}
            onClick={() => setActiveTab(TABS.clinics)}
          >
            Clinic Locations
          </button>
        </div>}

        {loading ? (
          <div style={styles.loadingState}>Loading users and clinics...</div>
        ) : (
          <>
            {activeTab === TABS.users && (
              <div>
                <div style={isPlatformOwner && !companyFilter ? styles.supportIntro : styles.sectionHeader}>
                  <div>
                    <h2 style={styles.sectionTitle}>{companyFilter ? 'Company Users' : isPlatformOwner ? 'Users by company' : 'System Users'}</h2>
                    {isPlatformOwner && !companyFilter && (
                      <div style={{ ...styles.subtle, marginTop: 5, fontSize: 13 }}>
                        Platform accounts and every company are kept in separate groups.
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <input
                      aria-label="Search users"
                      style={styles.searchInput}
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Search name, username or role"
                    />
                    <button type="button" style={styles.primaryButton} onClick={() => openAddUser()}>
                      Add User
                    </button>
                  </div>
                </div>

                {isPlatformOwner && !companyFilter ? (
                  companyUserGroups.length === 0
                    ? <div style={styles.emptyState}>No users or companies match this search.</div>
                    : companyUserGroups.map((group) => (
                      <section key={group.id} style={styles.companyGroup}>
                        <div style={styles.companyGroupHead}>
                          <div>
                            <h3 style={styles.companyGroupTitle}>{group.name}</h3>
                            <div style={styles.companyGroupMeta}>
                              {group.code || 'No code'}{group.domain ? ` · ${group.domain}` : ''} · {group.users.length} user{group.users.length === 1 ? '' : 's'}
                            </div>
                          </div>
                          {group.id !== 'platform' && group.id !== 'unassigned' && (
                            <button type="button" style={styles.groupAddButton} onClick={() => openAddUser(group.id)}>+ Add user</button>
                          )}
                        </div>
                        {renderUsersTable(group.users)}
                      </section>
                    ))
                ) : renderUsersTable(searchedUsers, true)}
              </div>
            )}

            {activeTab === TABS.clinics && (
              <div>
                <div style={styles.sectionHeader}>
                  <h2 style={styles.sectionTitle}>Clinic Locations</h2>
                  <button type="button" style={styles.primaryButton} onClick={() => setShowAddClinicModal(true)}>
                    Add Clinic
                  </button>
                </div>

                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Clinic Name</th>
                        <th style={styles.th}>Code</th>
                        <th style={styles.th}>Users</th>
                        <th style={styles.th}>Templates</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clinics.map(clinic => (
                        <tr key={clinic.id}>
                          <td style={styles.td}>{clinic.name}</td>
                          <td style={{ ...styles.td, ...styles.subtle }}>{clinic.code || '-'}</td>
                          <td style={styles.td}>{clinicUsage[clinic.id] || 0}</td>
                          <td style={styles.td}>
                            <button type="button" style={styles.linkButton}>
                              {Array.isArray(clinic.assignedForms) ? clinic.assignedForms.length : 0}
                            </button>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right' }}>
                            <div style={styles.actions}>
                              <button type="button" style={styles.outlineButton} onClick={() => handleEditClinic(clinic)}>
                                Edit
                              </button>
                              <button
                                type="button"
                                style={styles.dangerButton}
                                onClick={() => handleDeleteClinic(clinic.id, clinic.name)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {clinics.length === 0 && (
                  <div style={styles.emptyState}>No clinics found. Add a clinic to get started.</div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {showAddUserModal && (
        <div style={styles.overlay}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Add New User</h3>
              <button type="button" style={styles.closeButton} onClick={() => setShowAddUserModal(false)}>
                ×
              </button>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                style={styles.input}
                value={newUser.username}
                onChange={(event) => setNewUser({ ...newUser, username: event.target.value })}
                placeholder="e.g. jdoe"
                autoComplete="off"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Display Name</label>
              <input
                type="text"
                style={styles.input}
                value={newUser.displayName}
                onChange={(event) => setNewUser({ ...newUser, displayName: event.target.value })}
                placeholder="e.g. John Doe"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  style={{ ...styles.input, paddingRight: '40px' }}
                  value={newUser.password}
                  onChange={(event) => setNewUser({ ...newUser, password: event.target.value })}
                  placeholder="Min 6 characters"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(prev => !prev)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6b7280', display: 'flex', alignItems: 'center' }}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    {showNewPassword ? (
                      <path d="M3.27 2 2 3.27l3.04 3.04A11.89 11.89 0 0 0 1 12s4 7 11 7a10.9 10.9 0 0 0 4.69-1.04L20.73 22 22 20.73Zm8.8 8.8 2.13 2.13A3 3 0 0 1 12 15a2.94 2.94 0 0 1-2.07-.87l2.14-2.14Zm-.07-5.8c7 0 11 7 11 7a21.8 21.8 0 0 1-3.33 4.19l-1.43-1.43A10.75 10.75 0 0 0 20.36 12 14.32 14.32 0 0 0 12 7a10.3 10.3 0 0 0-2.76.37L7.57 5.7A12 12 0 0 1 12 5Z" fill="currentColor" />
                    ) : (
                      <path d="M12 5C5 5 1 12 1 12s4 7 11 7 11-7 11-7-4-7-11-7Zm0 11a4 4 0 1 1 4-4 4 4 0 0 1-4 4Zm0-6a2 2 0 1 0 2 2 2 2 0 0 0-2-2Z" fill="currentColor" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Role</label>
              <select
                style={styles.input}
                value={newUser.role}
                onChange={(event) => setNewUser({ ...newUser, role: event.target.value })}
              >
                <option value="staff">Staff</option>
                <option value="companyadmin">Company Admin</option>
                {isPlatformOwner && !companyFilter && !newUser.clinicId && <option value="superadmin">Platform Owner</option>}
              </select>
            </div>

            {newUser.role !== 'superadmin' && !companyFilter && (
              <div style={styles.field}>
                <label style={styles.label}>Assigned Clinic</label>
                <select
                  style={styles.input}
                  value={newUser.clinicId}
                  onChange={(event) => setNewUser({ ...newUser, clinicId: event.target.value })}
                >
                  <option value="">-- Select company --</option>
                  {clinics.filter(clinic => clinic.isActive !== false).map(clinic => (
                    <option key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={newUser.requiresTwoFactorSetup}
                  onChange={(event) => setNewUser({ ...newUser, requiresTwoFactorSetup: event.target.checked })}
                />
                <span>Require Two-Factor Authentication (2FA)</span>
              </label>
              <div style={styles.helper}>Default is off. When enabled, the user must set up 2FA on first login.</div>
            </div>

            <div style={styles.modalFooter}>
              <button type="button" style={styles.outlineButton} onClick={() => setShowAddUserModal(false)}>
                Cancel
              </button>
              <button type="button" style={styles.primaryButton} onClick={handleAddUser}>
                Save User
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditUserModal && editingUser && (
        <div style={styles.overlay}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Edit User</h3>
              <button type="button" style={styles.closeButton} onClick={() => setShowEditUserModal(false)}>
                ×
              </button>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                style={styles.input}
                value={editingUser.username}
                onChange={(event) => setEditingUser({ ...editingUser, username: event.target.value })}
                placeholder="e.g. jdoe"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Display Name</label>
              <input
                type="text"
                style={styles.input}
                value={editingUser.displayName}
                onChange={(event) => setEditingUser({ ...editingUser, displayName: event.target.value })}
                placeholder="e.g. John Doe"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showEditPassword ? 'text' : 'password'}
                  style={{ ...styles.input, paddingRight: '40px' }}
                  value={editingUser.password}
                  onChange={(event) => setEditingUser({ ...editingUser, password: event.target.value })}
                  placeholder="Leave blank to keep current password"
                />
                <button
                  type="button"
                  onClick={() => setShowEditPassword(prev => !prev)}
                  aria-label={showEditPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6b7280', display: 'flex', alignItems: 'center' }}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    {showEditPassword ? (
                      <path d="M3.27 2 2 3.27l3.04 3.04A11.89 11.89 0 0 0 1 12s4 7 11 7a10.9 10.9 0 0 0 4.69-1.04L20.73 22 22 20.73Zm8.8 8.8 2.13 2.13A3 3 0 0 1 12 15a2.94 2.94 0 0 1-2.07-.87l2.14-2.14Zm-.07-5.8c7 0 11 7 11 7a21.8 21.8 0 0 1-3.33 4.19l-1.43-1.43A10.75 10.75 0 0 0 20.36 12 14.32 14.32 0 0 0 12 7a10.3 10.3 0 0 0-2.76.37L7.57 5.7A12 12 0 0 1 12 5Z" fill="currentColor" />
                    ) : (
                      <path d="M12 5C5 5 1 12 1 12s4 7 11 7 11-7 11-7-4-7-11-7Zm0 11a4 4 0 1 1 4-4 4 4 0 0 1-4 4Zm0-6a2 2 0 1 0 2 2 2 2 0 0 0-2-2Z" fill="currentColor" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Role</label>
              <select
                style={styles.input}
                value={editingUser.role}
                onChange={(event) => setEditingUser({ ...editingUser, role: event.target.value })}
              >
                <option value="staff">Staff</option>
                <option value="companyadmin">Company Admin</option>
                {isPlatformOwner && !companyFilter && !editingUser.clinicId && <option value="superadmin">Platform Owner</option>}
              </select>
            </div>

            {editingUser.role !== 'superadmin' && !companyFilter && (
              <div style={styles.field}>
                <label style={styles.label}>Assigned Clinic</label>
                <select
                  style={styles.input}
                  value={editingUser.clinicId}
                  onChange={(event) => setEditingUser({ ...editingUser, clinicId: event.target.value })}
                >
                  <option value="">-- Select company --</option>
                  {clinics.filter(clinic => clinic.isActive !== false).map(clinic => (
                    <option key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={Boolean(editingUser.requiresTwoFactorSetup)}
                  onChange={(event) => setEditingUser({ ...editingUser, requiresTwoFactorSetup: event.target.checked })}
                />
                <span>Require Two-Factor Authentication (2FA)</span>
              </label>
              <div style={styles.helper}>Default is off. When enabled, the user must set up 2FA on next login.</div>
            </div>

            <div style={styles.modalFooter}>
              <button type="button" style={styles.outlineButton} onClick={() => setShowEditUserModal(false)}>
                Cancel
              </button>
              <button type="button" style={styles.primaryButton} onClick={handleUpdateUser}>
                Update User
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddClinicModal && (
        <div style={styles.overlay}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Add New Clinic</h3>
              <button type="button" style={styles.closeButton} onClick={() => setShowAddClinicModal(false)}>
                ×
              </button>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Clinic Name</label>
              <input
                type="text"
                style={styles.input}
                value={newClinic.name}
                onChange={(event) => setNewClinic({ name: event.target.value })}
                placeholder="e.g. Central Clinic"
              />
            </div>

            <div style={styles.modalFooter}>
              <button type="button" style={styles.outlineButton} onClick={() => setShowAddClinicModal(false)}>
                Cancel
              </button>
              <button type="button" style={styles.primaryButton} onClick={handleAddClinic}>
                Save Clinic
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditClinicModal && editingClinic && (
        <div style={styles.overlay}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Edit Clinic</h3>
              <button type="button" style={styles.closeButton} onClick={() => setShowEditClinicModal(false)}>
                ×
              </button>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Clinic Name</label>
              <input
                type="text"
                style={styles.input}
                value={editingClinic.name}
                onChange={(event) => setEditingClinic({ ...editingClinic, name: event.target.value })}
                placeholder="e.g. Central Clinic"
              />
            </div>

            <div style={styles.modalFooter}>
              <button type="button" style={styles.outlineButton} onClick={() => setShowEditClinicModal(false)}>
                Cancel
              </button>
              <button type="button" style={styles.primaryButton} onClick={handleUpdateClinic}>
                Update Clinic
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
